/**
 * SEO and JSON-LD for the place categories hub (`/place`).
 * Aligns with `$placeIdOrSlug.seo.ts`: absolute URLs, robots by environment, and a JSON-LD array
 * (`CollectionPage` + `BreadcrumbList`) for richer indexing.
 */

import allPlacesImage from '@web/src/assets/place/place-all.webp?w=1200&format=webp'
import beachImage from '@web/src/assets/place/place-beach.webp?w=1200&format=webp'
import campingImage from '@web/src/assets/place/place-camping.webp?w=1200&format=webp'
import clubImage from '@web/src/assets/place/place-club.webp?w=1200&format=webp'
import otherImage from '@web/src/assets/place/place-other.webp?w=1200&format=webp'
import resortImage from '@web/src/assets/place/place-resort.webp?w=1200&format=webp'
import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { SEO_META_DESCRIPTION_MAX_CHARS, truncateMetaDescription } from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath } from '@web/src/lib/utilsClient'
import type { PlaceCategoryEnum } from 'types/graphql'

export type PlaceCategoryCountsForSeo = {
  allPlaces: number
  nudeBeach: number
  resort: number
  club: number
  camping: number
  other: number
}

export type PlaceCategoriesIndexSeoLoaderInput = {
  placeCategoryCounts?: PlaceCategoryCountsForSeo | null
}

type TileSeoConfig = {
  titleKey: string
  imagePath: string
  category?: PlaceCategoryEnum
  countKey: keyof PlaceCategoryCountsForSeo
}

/** Same tiles as `index.tsx` cards — used only for structured data URLs and labels. */
const CATEGORY_TILES_FOR_JSON_LD: TileSeoConfig[] = [
  { titleKey: 'All Places', imagePath: allPlacesImage, countKey: 'allPlaces' },
  { titleKey: 'Nude Beaches', imagePath: beachImage, category: 'NUDE_BEACH', countKey: 'nudeBeach' },
  { titleKey: 'Resorts', imagePath: resortImage, category: 'RESORT', countKey: 'resort' },
  { titleKey: 'Clubs', imagePath: clubImage, category: 'CLUB', countKey: 'club' },
  { titleKey: 'Camping - Nude', imagePath: campingImage, category: 'CAMPING', countKey: 'camping' },
  { titleKey: 'Other', imagePath: otherImage, category: 'OTHER', countKey: 'other' },
]

/** Discover list URL with optional category filter (matches card navigation to `/place/list`). */
function absoluteDiscoverPlacesListUrl(category?: PlaceCategoryEnum): string {
  const base = absoluteUrlForPath('/place/list')
  const u = new URL(base)
  u.searchParams.set('page', '1')
  if (category) {
    u.searchParams.append('categories', category)
  }
  return u.href
}

function countForTile(tile: TileSeoConfig, counts: PlaceCategoryCountsForSeo | null | undefined): number {
  if (!counts) return 0
  return counts[tile.countKey] ?? 0
}

function buildPlaceCategoriesBreadcrumbJsonLd(pageUrl: string, tr: typeof i18n.t): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: tr('Home'), item: absoluteUrlForPath('/') },
      {
        '@type': 'ListItem',
        position: 2,
        name: tr('Categories'),
        item: pageUrl,
      },
    ],
  }
}

function buildPlaceCategoriesCollectionJsonLd(input: {
  tr: typeof i18n.t
  pageUrl: string
  pageTitle: string
  pageDescription: string
  counts: PlaceCategoryCountsForSeo | null | undefined
}): Record<string, unknown> {
  const { tr, pageUrl, pageTitle, pageDescription, counts } = input

  const itemListElement = CATEGORY_TILES_FOR_JSON_LD.map((tile, index) => {
    const title = tr(tile.titleKey)
    const listUrl = absoluteDiscoverPlacesListUrl(tile.category)
    const imageUrl = absoluteUrlForPath(tile.imagePath)
    const n = countForTile(tile, counts)
    const description = n > 0 ? tr('{{count}} Places', { count: n }) : undefined

    return {
      '@type': 'ListItem',
      position: index + 1,
      name: title,
      item: {
        '@type': 'WebPage',
        name: title,
        url: listUrl,
        image: imageUrl,
        ...(description ? { description } : {}),
      },
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: pageUrl,
    image: absoluteUrlForPath('/logo-og-image.webp'),
    mainEntity: {
      '@type': 'ItemList',
      name: tr('Place categories'),
      numberOfItems: CATEGORY_TILES_FOR_JSON_LD.length,
      itemListElement,
    },
  }
}

function buildMetaDescription(tr: typeof i18n.t, counts: PlaceCategoryCountsForSeo | null | undefined): string {
  const base = tr(
    'Discover lifestyle and swinger place categories on SpicyFriends: clubs, resorts, nude beaches, camping & more—browse filtered lists that match your vibe.'
  )
  if (counts != null && counts.allPlaces > 0) {
    const withCount = tr(
      'Discover lifestyle and swinger place categories on SpicyFriends: clubs, resorts, nude beaches, camping & more—{{count}} places to explore.',
      { count: counts.allPlaces }
    )
    if (withCount.length <= SEO_META_DESCRIPTION_MAX_CHARS) return withCount
    return truncateMetaDescription(withCount, SEO_META_DESCRIPTION_MAX_CHARS)
  }
  if (base.length <= SEO_META_DESCRIPTION_MAX_CHARS) return base
  return truncateMetaDescription(base, SEO_META_DESCRIPTION_MAX_CHARS)
}

export function placeCategoriesIndexHeadFromLoaderData(
  loaderData: PlaceCategoriesIndexSeoLoaderInput | null | undefined
) {
  const tr = i18n.t.bind(i18n)
  const counts = loaderData?.placeCategoryCounts ?? undefined
  const pageUrl = absoluteUrlForPath('/place')
  const pageTitle = tr('Find Your Next Place')
  const pageDescription = buildMetaDescription(tr, counts)

  const collectionPage = buildPlaceCategoriesCollectionJsonLd({
    tr,
    pageUrl,
    pageTitle,
    pageDescription,
    counts,
  })
  const breadcrumbList = buildPlaceCategoriesBreadcrumbJsonLd(pageUrl, tr)

  return seo({
    title: `${pageTitle} | ${env.VITE_APP_NAME}`,
    description: pageDescription,
    url: pageUrl,
    image: '/logo-og-image.webp',
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: [collectionPage, breadcrumbList],
  })
}
